import type { CSSProperties, ElementType, ReactNode } from 'react'

import { createElement, useEffect, useRef } from 'react'

import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'

type StaggerVariant = 'move' | 'fade' | 'scale'

interface StaggerProps {
    as?: ElementType
    variant?: StaggerVariant
    stagger?: number
    delay?: number
    duration?: number
    distance?: number
    fromScale?: number
    start?: string
    className?: string
    style?: CSSProperties
    children?: ReactNode
    [key: string]: unknown
}

/**
 * Reveals the direct children of a container one after another on scroll.
 * Mirrors Webflow's "Profile Pictures Move" (a-78) and similar grouped
 * reveals where each item animates with an incremental delay.
 */
export const Stagger = ({
    as = 'div',
    variant = 'scale',
    stagger = 0.12,
    delay = 0,
    duration = 0.6,
    distance = 25,
    fromScale = 0.5,
    start = 'top 85%',
    className,
    style,
    children,
    ...rest
}: StaggerProps) => {
    const ref = useRef<HTMLElement | null>(null)

    useEffect(() => {
        registerGsap()
        const el = ref.current
        if (!el || !el.children.length) return
        if (prefersReducedMotion() || isCompactViewport()) return

        const targets = Array.from(el.children)

        const fromVars: gsap.TweenVars = { opacity: 0 }
        const toVars: gsap.TweenVars = { opacity: 1, duration, delay, ease: 'wfEase', stagger }

        if (variant === 'move') {
            fromVars.y = distance
            toVars.y = 0
        }
        if (variant === 'scale') {
            fromVars.scale = fromScale
            toVars.scale = 1
        }

        const ctx = gsap.context(() => {
            gsap.fromTo(targets, fromVars, {
                ...toVars,
                scrollTrigger: { trigger: el, start, once: true }
            })
        }, el)

        return () => ctx.revert()
    }, [variant, stagger, delay, duration, distance, fromScale, start])

    return createElement(as, { ref, className, style, ...rest }, children)
}
